import React, { useState, useRef, useEffect, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import FileUpload from './components/FileUpload';
import AnalysisResults from './components/AnalysisResults';
import LoadingState from './components/LoadingState';
import PaywallModal from './components/PaywallModal';
import SiteNav from './components/SiteNav';
import SiteFooter from './components/SiteFooter';
import Home from './pages/Home';
import Pricing from './pages/Pricing';
import History from './pages/History';
import SignIn from './pages/SignIn';
import { GuidesIndex, GuidePage } from './pages/Guides';
import PageMeta from './PageMeta';
import { ErrorIllustration } from './components/Illustrations';
import {
  API_URL,
  loadPlan, savePlan, clearPlan, isProActive, formatPeriodEnd,
  loadPending, savePending, clearPending,
  startCheckout, confirmCheckout, refreshPlan, cancelPlan,
  requestLogin, confirmLogin, confirmRestore, fetchReport,
} from './billing';
import './App.css';
import './site.css';
import './home.css';
import './analyze.css';
import './guides.css';

const HISTORY_KEY = 'propdoc_history';
const PAYWALL_DELAY_MS = 2500;
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_TRIES = 100; // ~5 minutes

function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveToHistory(data) {
  const history = loadHistory();
  const entry = { id: Date.now(), date: new Date().toISOString(), data };
  const updated = [entry, ...history.filter(e => e.data.filename !== data.filename)].slice(0, 15);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  return updated;
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <ErrorIllustration />
          <h2>Something Went Wrong</h2>
          <p className="error-message">An unexpected error occurred. Please refresh the page and try again.</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Scroll to in-page anchors on navigation (react-router doesn't by default),
// otherwise start each page from the top.
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function AppShell() {
  const [state, setState] = useState('idle');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [filename, setFilename] = useState('');
  const [history, setHistory] = useState(loadHistory);
  const [plan, setPlan] = useState(loadPlan);
  const [billingLive, setBillingLive] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [notice, setNotice] = useState(null);
  const [loadingStage, setLoadingStage] = useState(null); // real pipeline stage from the server
  const reportRef = useRef(null); // { reportId, filename } for the in-flight analysis
  const paywallTimerRef = useRef(null);
  const abortRef = useRef(null);
  const navigate = useNavigate();

  function revealResults(data) {
    setResults(data);
    setState('results');
    setHistory(saveToHistory(data));
    setPaywallOpen(false);
    setReportReady(false);
    reportRef.current = null;
    clearPending();
    navigate('/analyze');
  }

  function applyPlan(newPlan) {
    savePlan(newPlan);
    setPlan(newPlan);
  }

  // Handle returns from Stripe checkout / restore links, and refresh
  // soon-to-expire Pro tokens against the server.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    const restoreCode = params.get('restore');
    const loginCode = params.get('login');
    if (checkout || restoreCode || loginCode) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (checkout === 'success' && sessionId) {
      finishCheckout(sessionId);
    } else if (checkout === 'cancelled') {
      resumeAfterCancelledCheckout();
    } else if (loginCode) {
      finishLogin(loginCode);
    } else if (restoreCode) {
      finishRestore(restoreCode);
    } else {
      resumeInterruptedUnlock();
      maybeRefreshPlan();
    }

    fetch(`${API_URL}/api/health`)
      .then(r => r.json())
      .then(h => setBillingLive(h.billing !== false))
      .catch(() => {});
  }, []);

  async function maybeRefreshPlan() {
    const current = loadPlan();
    if (!isProActive(current) && current?.type?.startsWith('pro')) {
      // Expired pro token: see if the subscription is still alive.
      const refreshed = await refreshPlan(current);
      if (refreshed) applyPlan(refreshed);
      else { clearPlan(); setPlan(null); }
      return;
    }
    if (!current?.type?.startsWith('pro') || !current.exp) return;
    // Refresh in the last 24h before expiry so renewals roll over seamlessly.
    if (current.exp * 1000 - Date.now() < 24 * 3600 * 1000) {
      const refreshed = await refreshPlan(current);
      if (refreshed) applyPlan(refreshed);
      else { clearPlan(); setPlan(null); }
    }
  }

  async function pollReveal(reportId, token, pendingFilename) {
    setState('loading');
    setFilename(pendingFilename || 'your document');
    navigate('/analyze');
    for (let i = 0; i < POLL_MAX_TRIES; i++) {
      const { status, data } = await fetchReport(reportId, token);
      if (status === 200) { revealResults(data); return; }
      if (status === 202 || status === 402) {
        if (data?.stage) setLoadingStage(data.stage);
        await sleep(POLL_INTERVAL_MS);
        continue;
      }
      setError(data.error || 'Something went wrong retrieving your report.');
      setState('error');
      clearPending();
      return;
    }
    setError('The analysis is taking longer than expected. Please try uploading again.');
    setState('error');
  }

  async function finishCheckout(sessionId) {
    const pending = loadPending();
    try {
      const confirmed = await confirmCheckout(sessionId);
      if (confirmed.topup) {
        setNotice(`${confirmed.topup.added} extra reports added — ${confirmed.topup.limit} available this month.`);
        return;
      }
      const newPlan = confirmed.plan;
      if (newPlan.type?.startsWith('pro')) {
        applyPlan(newPlan);
      } else if (pending) {
        // Keep the single-report token around in case the tab is refreshed
        // while the analysis is still finishing.
        savePending({ ...pending, singleToken: newPlan.token });
      }
      const reportId = pending?.reportId || newPlan.reportId;
      if (!reportId) {
        // Plan purchased from the pricing page — no report to reveal.
        setNotice('Pro plan active — head to Analyse to run your first report.');
        return;
      }
      setState('loading');
      setFilename(pending?.filename || 'your document');
      await pollReveal(reportId, newPlan.token, pending?.filename);
    } catch (err) {
      setError(err.message);
      setState('error');
      navigate('/analyze');
    }
  }

  // The user backed out of checkout while the analysis may still be running:
  // show the paywall again over the loading screen and watch for completion.
  async function resumeAfterCancelledCheckout() {
    const pending = loadPending();
    if (!pending?.reportId) return;
    reportRef.current = pending;
    setState('loading');
    setFilename(pending.filename || 'your document');
    setPaywallOpen(true);
    navigate('/analyze');
    for (let i = 0; i < POLL_MAX_TRIES; i++) {
      const { status, data } = await fetchReport(pending.reportId, null);
      if (status === 402) {
        if (data.status === 'ready') { setReportReady(true); return; }
        if (data?.stage) setLoadingStage(data.stage);
        await sleep(POLL_INTERVAL_MS);
        continue;
      }
      if (status === 200) { revealResults(data); return; } // server not gating
      // 404 (expired) or error — reset quietly.
      setPaywallOpen(false);
      clearPending();
      setError(data.error || 'This report has expired. Please upload the document again.');
      setState('error');
      return;
    }
  }

  // Tab refreshed after paying for a single report but before it was revealed.
  function resumeInterruptedUnlock() {
    const pending = loadPending();
    if (pending?.reportId && pending.singleToken) {
      pollReveal(pending.reportId, pending.singleToken, pending.filename);
    }
  }

  async function finishLogin(code) {
    try {
      const newPlan = await confirmLogin(code);
      applyPlan(newPlan);
      setNotice(newPlan.email ? `Signed in as ${newPlan.email} — Pro is active on this device.` : 'Signed in — Pro is active on this device.');
    } catch (err) {
      setNotice(err.message);
    }
  }

  async function finishRestore(code) {
    try {
      const newPlan = await confirmRestore(code);
      applyPlan(newPlan);
      setNotice('Pro access restored on this device.');
    } catch (err) {
      setNotice(err.message);
    }
  }

  // The server held a report even though this device looks Pro — usually a
  // stale or out-of-sync token. Refresh the entitlement and retry once before
  // ever showing a subscriber the paywall.
  async function tryProUnlock(reportId, activePlan) {
    const refreshed = await refreshPlan(activePlan);
    if (!refreshed) { clearPlan(); setPlan(null); return false; }
    if (refreshed !== activePlan) applyPlan(refreshed);
    const { status, data } = await fetchReport(reportId, refreshed.token);
    if (status === 200) { revealResults(data); return true; }
    return false;
  }

  async function handleUpload(file) {
    setState('loading');
    setError(null);
    setFilename(file.name);
    setReportReady(false);
    setLoadingStage(null);

    // A Pro token that expired mid-session (e.g. the tab stayed open across a
    // renewal) usually still belongs to a live subscription — refresh it
    // instead of treating the subscriber as a free user.
    let activePlan = plan;
    if (!isProActive(activePlan) && activePlan?.type?.startsWith('pro')) {
      const refreshed = await refreshPlan(activePlan);
      if (!refreshed) { clearPlan(); setPlan(null); activePlan = null; }
      else if (isProActive(refreshed)) { applyPlan(refreshed); activePlan = refreshed; }
    }
    const proActive = isProActive(activePlan);
    abortRef.current = new AbortController();
    // Pro subscribers go straight through; everyone else gets the paywall
    // a couple of seconds into the loading state.
    if (!proActive && billingLive) {
      paywallTimerRef.current = setTimeout(() => setPaywallOpen(true), PAYWALL_DELAY_MS);
    }

    const formData = new FormData();
    formData.append('pdf', file);

    try {
      const res = await fetch(`${API_URL}/api/analyze`, {
        method: 'POST',
        body: formData,
        signal: abortRef.current.signal,
        headers: proActive ? { Authorization: `Bearer ${activePlan.token}` } : {},
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Server error (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.trim()) continue;
          const eventMatch = part.match(/^event:\s*(\S+)/m);
          const dataMatch = part.match(/^data:\s*(.+)$/m);
          if (!eventMatch || !dataMatch) continue;

          const eventType = eventMatch[1];
          if (eventType === 'heartbeat') continue;

          const data = JSON.parse(dataMatch[1]);
          if (eventType === 'accepted') {
            reportRef.current = { reportId: data.reportId, filename: file.name };
            savePending(reportRef.current);
          } else if (eventType === 'progress') {
            setLoadingStage(data);
          } else if (eventType === 'result') {
            // Only sent when entitled (Pro) or billing is disabled server-side.
            clearTimeout(paywallTimerRef.current);
            revealResults(data);
            return;
          } else if (eventType === 'locked') {
            // Report finished and is being held server-side pending payment.
            clearTimeout(paywallTimerRef.current);
            // A Pro device should never be asked to pay — retry with a
            // freshly refreshed token before falling back to the paywall.
            if (proActive && await tryProUnlock(data.reportId, activePlan)) return;
            setReportReady(true);
            setPaywallOpen(true);
            return;
          } else if (eventType === 'error') {
            throw new Error(data.error || 'Analysis failed');
          }
        }
      }

      // The stream closed without a result/locked/error event — a server
      // redeploy or dropped connection mid-analysis, not a finished request.
      // The report keeps running (and is held) server-side, so poll for it
      // instead of leaving the loading bar stuck forever.
      const pendingReport = reportRef.current;
      if (pendingReport?.reportId) {
        for (let i = 0; i < POLL_MAX_TRIES; i++) {
          const { status, data } = await fetchReport(pendingReport.reportId, proActive ? activePlan.token : null);
          if (status === 200) {
            clearTimeout(paywallTimerRef.current);
            revealResults(data);
            return;
          }
          if (status === 402) {
            if (data.status === 'ready') {
              clearTimeout(paywallTimerRef.current);
              if (proActive && await tryProUnlock(pendingReport.reportId, activePlan)) return;
              setReportReady(true);
              setPaywallOpen(true);
              return;
            }
            if (data?.stage) setLoadingStage(data.stage);
          } else if (status === 202) {
            if (data?.stage) setLoadingStage(data.stage);
          } else {
            // 404: the server restarted and the held report is gone.
            throw new Error(data.error || 'The connection to the analysis server was interrupted. Please upload the document again.');
          }
          await sleep(POLL_INTERVAL_MS);
        }
      }
      throw new Error('The connection to the analysis server was interrupted. Please upload the document again.');
    } catch (err) {
      if (err.name === 'AbortError') return; // user closed the paywall
      clearTimeout(paywallTimerRef.current);
      setPaywallOpen(false);
      setReportReady(false);
      reportRef.current = null;
      clearPending();
      if (!navigator.onLine) {
        setError('You appear to be offline. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'Failed to analyze document. Please try again.');
      }
      setState('error');
    }
  }

  // Create a Stripe Checkout session and hand the browser over to it.
  // The analysis keeps running server-side while the user pays.
  async function handlePurchase(planType) {
    const url = await startCheckout(planType, reportRef.current?.reportId);
    window.location.assign(url);
    // Keep the button spinner going until the page actually unloads.
    await sleep(15000);
  }

  async function handleRestore(email) {
    return requestLogin(email);
  }

  function handleSignOut() {
    clearPlan();
    setPlan(null);
    setNotice('Signed out on this device. Your subscription is unaffected — sign back in anytime.');
  }

  function handlePaywallClose() {
    clearTimeout(paywallTimerRef.current);
    abortRef.current?.abort();
    setPaywallOpen(false);
    setReportReady(false);
    reportRef.current = null;
    clearPending();
    handleReset();
  }

  async function handleCancelPlan() {
    if (!window.confirm('Cancel your Pro plan? You keep access until the end of the current billing period.')) return;
    try {
      const periodEnd = await cancelPlan(plan);
      applyPlan({ ...plan, cancelAtPeriodEnd: true, periodEnd });
      setNotice(`Pro plan cancelled — access continues until ${formatPeriodEnd(periodEnd) || 'the end of the billing period'}.`);
    } catch (err) {
      // Subscription already gone on Stripe's side: drop the local plan.
      clearPlan();
      setPlan(null);
      setNotice(err.message);
    }
  }

  function handleReset() {
    setState('idle');
    setResults(null);
    setError(null);
    setFilename('');
  }

  function handleLoadHistory(data) {
    setResults(data);
    setState('results');
    navigate('/analyze');
  }

  function handleClearHistory() {
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  }

  const analyzePage = (
    <main className="app-main analyze-page">
      <PageMeta
        title="Analyse your Section 32 — Prop Doc"
        path="/analyze"
        description="Upload your Victorian Section 32 or Contract of Sale PDF and get a plain-English report in about two minutes: risks, easements, fees and questions to ask."
      />
      {state === 'idle' && <FileUpload onUpload={handleUpload} />}
      {state === 'loading' && <LoadingState filename={filename} stage={loadingStage} />}
      {state === 'error' && (
        <div className="error-container">
          <ErrorIllustration />
          <h2>Analysis Failed</h2>
          <p className="error-message">{error}</p>
          <div className="error-tips">
            <h4>Common solutions:</h4>
            <ul>
              <li>Make sure the file is a valid PDF document</li>
              <li>Try a smaller file (under 50 pages works best)</li>
              <li>Check that the document is a Section 32/Contract of Sale</li>
              <li>Ensure you have a stable internet connection</li>
            </ul>
          </div>
          <button className="btn btn-primary" onClick={handleReset}>Try Again</button>
        </div>
      )}
      {state === 'results' && results && (
        <AnalysisResults data={results} onReset={handleReset} />
      )}
    </main>
  );

  return (
    <ErrorBoundary>
      <div className="app">
        <ScrollManager />
        <SiteNav proActive={isProActive(plan)} historyCount={history.length} />

        {notice && (
          <div className="app-notice" role="status">
            <span>{notice}</span>
            <button onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
          </div>
        )}

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/analyze" element={analyzePage} />
          <Route path="/guides" element={<GuidesIndex />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
          <Route
            path="/pricing"
            element={
              <Pricing
                plan={plan}
                billingLive={billingLive}
                onPurchase={handlePurchase}
                onCancelPlan={handleCancelPlan}
              />
            }
          />
          <Route
            path="/history"
            element={
              <History
                history={history}
                onLoad={handleLoadHistory}
                onClear={handleClearHistory}
                plan={plan}
                onCancelPlan={handleCancelPlan}
                onSignIn={requestLogin}
                onSignOut={handleSignOut}
              />
            }
          />
          <Route
            path="/signin"
            element={
              <SignIn
                plan={plan}
                onCancelPlan={handleCancelPlan}
                onSignIn={requestLogin}
                onSignOut={handleSignOut}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <PaywallModal
          open={paywallOpen}
          analysisReady={reportReady}
          onPurchase={handlePurchase}
          onRestore={handleRestore}
          onClose={handlePaywallClose}
        />

        <SiteFooter />
      </div>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
